import { useFieldContext } from '@/lib/form.ts';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { COMMON_VALIDATION_ERRORS, ZodIssueMap } from '@/common/form/utils.ts';
import { t } from '@lingui/core/macro';
import { FormFieldErrors } from '@/common/components/form-field-errors/FormFieldErrors.tsx';
import { useShouldShowFieldErrors } from '@/common/hooks/useShouldShowFieldErrors.ts';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { InputProps } from '@mui/material/Input';
import { useLingui } from '@lingui/react/macro';

const zodIssueMap: ZodIssueMap = {
  invalidType: COMMON_VALIDATION_ERRORS.required,
  tooSmall: (issue) => {
    const minimum = issue.minimum as number;
    return t`Minimum length is ${minimum}`;
  },
  tooBig: (issue) => {
    const maximum = issue.maximum as number;
    return t`Maximum length is ${maximum}`;
  },
};

export type FormTextFieldProps = Omit<
  TextFieldProps,
  'error' | 'value' | 'onChange' | 'onBlur' | 'helperText'
> & {
  errorMessage?: string;
  isLoading?: boolean;
};

export const FormTextField = ({
  variant = 'outlined',
  size = 'small',
  fullWidth = true,
  errorMessage,
  isLoading,
  ...rest
}: FormTextFieldProps) => {
  const field = useFieldContext<string | null>();
  const { shouldShowFieldErrors, onBlur } = useShouldShowFieldErrors(!!errorMessage);
  const { t } = useLingui();

  const handleBlur = () => {
    field.handleBlur();
    onBlur();
  };

  return (
    <TextField
      {...rest}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      value={field.state.value ?? ''}
      onChange={(e) => field.handleChange(e.target.value || null)}
      onBlur={handleBlur}
      error={shouldShowFieldErrors}
      slotProps={{
        ...rest.slotProps,
        input: {
          ...(rest.slotProps?.input ?? {}),
          endAdornment: isLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={16} aria-label={t`Loading`} />
            </InputAdornment>
          ) : (
            (rest.slotProps?.input as InputProps | undefined)?.endAdornment
          ),
        },
      }}
      helperText={
        shouldShowFieldErrors && (
          <>
            <FormFieldErrors zodIssueMap={zodIssueMap} />
            {errorMessage && (
              <Box data-testid={`${field.name}-error`} component="span" sx={{ display: 'block' }}>
                {errorMessage}
              </Box>
            )}
          </>
        )
      }
    />
  );
};
